// Umowa danych (plan, wersja 2, sekcja 4.2).
// Wszystko, co dzieje się w aplikacji, jest zdarzeniem dopisywanym do dziennika. Niczego się nie nadpisuje ani nie kasuje:
// to, co widzi użytkownik (stan), liczy się z dziennika funkcją `stan()`. Poprawka = nowe zdarzenie z autorem.

export const WERSJA_SCHEMATU = 1;

export const TYPY = Object.freeze({
  BUDOWA_UTWORZONA: 'budowa.utworzona',
  BUDOWA_ZMIENIONA: 'budowa.zmieniona',
  BUDOWA_UKRYTA: 'budowa.ukryta',
  BUDOWA_PRZYWROCONA: 'budowa.przywrocona',
  DOKUMENT_DODANY: 'dokument.dodany',
  DOKUMENT_ZMIENIONY: 'dokument.zmieniony',
  DOKUMENT_UKRYTY: 'dokument.ukryty',
  PIETRO_DODANE: 'pietro.dodane',
  PIETRO_SKALA: 'pietro.skala',
  POMIESZCZENIE_DODANE: 'pomieszczenie.dodane',
  POMIESZCZENIE_OBRYS: 'pomieszczenie.obrys',
  ROZDZIELNICA_DODANA: 'rozdzielnica.dodana',
  OBWOD_DODANY: 'obwod.dodany',
  PUNKT_DODANY: 'punkt.dodany',
  PUNKT_POMIESZCZENIE: 'punkt.pomieszczenie',
  TRASA_DODANA: 'trasa.dodana',
  POMIAR_ZAPISANY: 'pomiar.zapisany',
  PRACA_ODHACZONA: 'praca.odhaczona',
  PRACA_COFNIETA: 'praca.cofnieta',
  ZMIANA_ZAPROPONOWANA: 'zmiana.zaproponowana',
  ZMIANA_ZATWIERDZONA: 'zmiana.zatwierdzona',
  ZMIANA_ODRZUCONA: 'zmiana.odrzucona',
  BLOKADA_ZGLOSZONA: 'blokada.zgloszona',
  BLOKADA_ZDJETA: 'blokada.zdjeta',
  USTERKA_DODANA: 'usterka.dodana',
  USTERKA_USUNIETA: 'usterka.usunieta',
  ODBIOR_ZAPISANY: 'odbior.zapisany',
  GOTOWOSC_ZGLOSZONA: 'gotowosc.zgloszona',
  LV_WCZYTANE: 'lv.wczytane',
  LV_PRZYPISANE: 'lv.przypisane',
});

export const RODZAJE_ZMIAN = Object.freeze(['miejsce', 'osprzet', 'usuniecie']);

export function uuid() {
  if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
  // zapas dla starych przeglądarek
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Czas urządzenia rośnie ściśle: dwa zdarzenia w tej samej milisekundzie (np. zmiana i jej zatwierdzenie) dostają kolejne milisekundy,
// bo przy remisie kolejność rozstrzyga losowy identyfikator i zatwierdzenie mogło trafić przed propozycję (błąd z 13.10, test „rodzaj naraz”).
let ostatniCzas = 0;
const teraz = () => { const t = Math.max(Date.now(), ostatniCzas + 1); ostatniCzas = t; return new Date(t).toISOString(); };

/** Tworzy zdarzenie z wszystkimi polami wymaganymi przez umowę danych. */
export function zdarzenie(typ, dane, kontekst) {
  if (!Object.values(TYPY).includes(typ)) throw new Error(`nieznany typ zdarzenia: ${typ}`);
  const { firmaId, budowaId, autor, indeksPlanu = null, czas = teraz() } = kontekst || {};
  if (!firmaId || !budowaId || !autor) throw new Error('zdarzenie wymaga firmaId, budowaId i autora');
  return Object.freeze({
    id: uuid(), schemat: WERSJA_SCHEMATU, typ, firmaId, budowaId, autor,
    czasUrzadzenia: czas, czasSerwera: null, indeksPlanu, dane: Object.freeze({ ...dane }),
  });
}

/** Podnosi stare zdarzenia do bieżącego schematu (migracja w locie, oryginał zostaje w dzienniku bez zmian). */
export function podnies(z) {
  let e = z;
  // przykład na przyszłość: if (e.schemat === 1) e = { ...e, schemat: 2, ... };
  if (e.schemat > WERSJA_SCHEMATU) throw new Error(`zdarzenie ze schematu ${e.schemat}, aplikacja zna ${WERSJA_SCHEMATU}: zaktualizuj aplikację`);
  return e;
}

// Kolejność stosowania: czas urządzenia, a przy remisie identyfikator. Ta sama kolejność na każdym urządzeniu.
const porzadek = (a, b) => (a.czasUrzadzenia < b.czasUrzadzenia ? -1 : a.czasUrzadzenia > b.czasUrzadzenia ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Liczy stan jednej budowy z dziennika. Wynik nie zależy od kolejności, w jakiej zdarzenia dotarły (synchronizacja).
 * Reguły konfliktów:
 * - odhaczenie: praca jest zrobiona, jeśli istnieje odhaczenie, którego nie cofnięto wpisem wskazującym DOKŁADNIE to odhaczenie.
 *   Dwa telefony offline odhaczają tę samą pracę → zostają oba wpisy (dwóch autorów), cofnięcie jednego nie cofa drugiego („zrobione wygrywa”).
 * - zmiana planu: propozycja czeka na zatwierdzenie; dwie zatwierdzone zmiany tego samego punktu oparte na tej samej wersji punktu
 *   to konflikt do decyzji kierownika (lista `konflikty`), pierwsza w kolejności jest stosowana.
 */
export function stan(zdarzenia, budowaId) {
  const S = { budowa: null, ukryta: false, dokumenty: {}, trasy: {}, pomiary: {}, blokady: {}, usterki: {}, odbiory: {}, lv: null, lvPrzypisania: {}, gotowosc: {}, pietra: {}, pomieszczenia: {}, rozdzielnice: {}, obwody: {}, punkty: {},
    odhaczenia: {}, prace: {}, zmiany: {}, konflikty: [], historia: [], bledne: [] };
  const lista = zdarzenia.filter((z) => z.budowaId === budowaId).map(podnies).sort(porzadek);
  const cofniete = new Set(lista.filter((z) => z.typ === TYPY.PRACA_COFNIETA).map((z) => z.dane.odhaczenieId));
  for (const z of lista) {
    const d = z.dane;
    S.historia.push({ id: z.id, typ: z.typ, autor: z.autor, czas: z.czasUrzadzenia });
    // błędny wpis (np. z innego urządzenia albo starszej wersji) nie może zablokować całej budowy: pomijamy go i zapisujemy w `bledne`
    try {
    switch (z.typ) {
      case TYPY.BUDOWA_UTWORZONA: S.budowa = { id: budowaId, nazwa: d.nazwa, kod: d.kod, umowa: d.umowa || 'VOB/B', land: d.land || null, utworzona: z.czasUrzadzenia, autor: z.autor }; break;
      case TYPY.BUDOWA_ZMIENIONA: if (S.budowa) Object.assign(S.budowa, pick(d, ['nazwa', 'kod', 'umowa', 'land'])); break;
      case TYPY.BUDOWA_UKRYTA: S.ukryta = true; break;
      case TYPY.BUDOWA_PRZYWROCONA: S.ukryta = false; break;
      // dokument: plik (plan, legenda, LV, schemat, inne) zapisany pod skrótem SHA-256; nowy indeks planu = nowy dokument z `zastepuje`
      case TYPY.DOKUMENT_DODANY: S.dokumenty[d.id] = { id: d.id, hash: d.hash, nazwa: d.nazwa, rodzaj: d.rodzaj || 'inne', mime: d.mime, rozmiar: d.rozmiar,
        stron: d.stron ?? null, folder: d.folder || null, pietroId: d.pietroId || null, indeks: d.indeks || null, zastepuje: d.zastepuje || null, ukryty: false, dodany: z.czasUrzadzenia, autor: z.autor }; break;
      case TYPY.DOKUMENT_ZMIENIONY: if (S.dokumenty[d.id]) Object.assign(S.dokumenty[d.id], pick(d, ['nazwa', 'rodzaj', 'pietroId', 'indeks'])); break;
      case TYPY.DOKUMENT_UKRYTY: if (S.dokumenty[d.id]) S.dokumenty[d.id].ukryty = true; break;
      // piętro: dokument z rzutem + kalibracja (punkt zerowy i punkty PDF na metr), żeby rysować pomieszczenia na planie
      case TYPY.PIETRO_DODANE: S.pietra[d.id] = { id: d.id, nazwa: d.nazwa, kolejnosc: d.kolejnosc ?? 0, dokumentId: d.dokumentId || null, kalibracja: d.kalibracja || null, skala: +d.skala || null }; break;
      // skala planu (1:50 → 50): przesunięcie punktu w cm rzeczywistych przeliczane na plan, także bez kalibracji
      case TYPY.PIETRO_SKALA: if (S.pietra[d.id]) S.pietra[d.id].skala = +d.skala || null; break;
      case TYPY.POMIESZCZENIE_DODANE: S.pomieszczenia[d.id] = { id: d.id, pietroId: d.pietroId, numer: d.numer, nazwa: d.nazwa || '', sciana: d.sciana || 'murowana', strefa: !!d.strefa, geom: d.geom || null, ...(d.pomocnicze ? { pomocnicze: true } : {}) }; break;
      // obrys pomieszczenia na rzucie (punkty PDF strony bez obrotu), narysowany palcem; nowszy obrys zastępuje starszy
      case TYPY.POMIESZCZENIE_OBRYS: if (S.pomieszczenia[d.id]) S.pomieszczenia[d.id].obrys = (d.obrys || []).map(([x, y]) => [+x, +y]); break;
      case TYPY.ROZDZIELNICA_DODANA: S.rozdzielnice[d.id] = { id: d.id, nazwa: d.nazwa, pietroId: d.pietroId || null, x: d.x ?? null, y: d.y ?? null }; break;
      // trasa: łańcuch odcinków, każdy w jednym pomieszczeniu albo strefie (K7 b)
      case TYPY.TRASA_DODANA: (S.trasy ||= {})[d.id] = { id: d.id, typ: d.typ, pietroId: d.pietroId, odcinki: (d.odcinki || []).map((o) => ({ id: o.id, pomieszczenieId: o.pomieszczenieId, m: o.m })) }; break;
      // pomiar obwodu (K3 b): wartości VDE 0100-600, wynik, autor; nowszy wpis tego samego obwodu zastępuje widok, stary zostaje w historii
      case TYPY.POMIAR_ZAPISANY: (S.pomiary ||= {})[d.obwodId] = { obwodId: d.obwodId, wartosci: d.wartosci || {}, wynik: d.wynik, przyrzad: d.przyrzad || null, autor: z.autor, czas: z.czasUrzadzenia, id: z.id }; break;
      case TYPY.OBWOD_DODANY: S.obwody[d.id] = { id: d.id, nazwa: d.nazwa, rozdzielnicaId: d.rozdzielnicaId, rcd: d.rcd || null, zabezpieczenie: d.zabezpieczenie || null }; break;
      case TYPY.PUNKT_DODANY: S.punkty[d.id] = { id: d.id, typ: d.typ, pomieszczenieId: d.pomieszczenieId, obwodId: d.obwodId || null, x: d.x, y: d.y, osprzet: d.osprzet || null, wersja: 1, usuniety: false, zPlanu: d.zPlanu || null, doSprawdzenia: !!d.doSprawdzenia }; break;
      // ręczne przypisanie punktu z planu do pomieszczenia (lista „punkty poza obrysami”): poprawka struktury, nie zmiana na budowie; wygrywa z obrysem
      case TYPY.PUNKT_POMIESZCZENIE: { const p = S.punkty[d.id]; if (p && S.pomieszczenia[d.pomieszczenieId]) Object.assign(p, { pomieszczenieId: d.pomieszczenieId, recznie: true, doSprawdzenia: false }); break; }
      case TYPY.PRACA_ODHACZONA:
        if (!cofniete.has(z.id)) {
          const k = klucz(d.obiektId, d.pracaId);
          (S.odhaczenia[k] ||= []).push({ id: z.id, autor: z.autor, czas: z.czasUrzadzenia, wartosc: d.wartosc ?? null, zdjecia: d.zdjecia || [], indeksPlanu: z.indeksPlanu });
        }
        break;
      case TYPY.PRACA_COFNIETA: break; // skutek policzony wyżej przez zbiór `cofniete`
      case TYPY.ZMIANA_ZAPROPONOWANA:
        if (!RODZAJE_ZMIAN.includes(d.rodzaj)) throw new Error(`nieznany rodzaj zmiany: ${d.rodzaj}`);
        S.zmiany[d.zmianaId] = { id: d.zmianaId, rodzaj: d.rodzaj, punktId: d.punktId, wersjaPunktu: d.wersjaPunktu, nowe: d.nowe || {},
          opis: d.opis || '', polecil: d.polecil || null, zdjecia: d.zdjecia || [], skutki: d.skutki || [], nachtrag: !!d.nachtrag, status: 'czeka', autor: z.autor, czas: z.czasUrzadzenia };
        break;
      case TYPY.ZMIANA_ZATWIERDZONA: {
        const m = S.zmiany[d.zmianaId]; const p = m && S.punkty[m.punktId];
        if (!m || m.status !== 'czeka') break;
        if (!p || p.wersja !== m.wersjaPunktu) { m.status = 'konflikt'; S.konflikty.push({ zmianaId: m.id, punktId: m.punktId, powod: 'punkt zmieniony w międzyczasie' }); break; }
        m.status = 'zatwierdzona'; m.zatwierdzil = z.autor;
        if (m.rodzaj === 'miejsce') { Object.assign(p, pick(m.nowe, ['x', 'y', 'pomieszczenieId'])); if (m.nowe.zPlanu && p.zPlanu) p.zPlanu = { ...p.zPlanu, x: +m.nowe.zPlanu.x, y: +m.nowe.zPlanu.y }; p.przeniesiony = true; }
        if (m.rodzaj === 'osprzet') Object.assign(p, pick(m.nowe, ['osprzet', 'typ']));
        if (m.rodzaj === 'usuniecie') p.usuniety = true;
        p.wersja += 1;
        break;
      }
      case TYPY.ZMIANA_ODRZUCONA: if (S.zmiany[d.zmianaId]?.status === 'czeka') Object.assign(S.zmiany[d.zmianaId], { status: 'odrzucona', odrzucil: z.autor }); break;
      // blokada pracy (czeka na tynk / g-k / sufit / posadzkę): dowód do Behinderungsanzeige (VOB/B §6), zdjęcie znosi ją z historią
      case TYPY.BLOKADA_ZGLOSZONA: S.blokady[d.id] = { id: d.id, pomieszczenieId: d.pomieszczenieId, rodzaj: d.rodzaj, opis: d.opis || '', zdjecia: d.zdjecia || [], od: z.czasUrzadzenia, autor: z.autor, aktywna: true }; break;
      case TYPY.BLOKADA_ZDJETA: if (S.blokady[d.id]) Object.assign(S.blokady[d.id], { aktywna: false, do: z.czasUrzadzenia, zdjal: z.autor }); break;
      // usterka: opis, termin, zdjęcie przed; usunięcie ze zdjęciem po
      case TYPY.USTERKA_DODANA: S.usterki[d.id] = { id: d.id, pomieszczenieId: d.pomieszczenieId, punktId: d.punktId || null, opis: d.opis || '', termin: d.termin || null, zdjeciaPrzed: d.zdjecia || [], zdjeciaPo: [], otwarta: true, dodana: z.czasUrzadzenia, autor: z.autor }; break;
      // odbiór (Abnahme, VOB/B §12): zakres (piętro / pomieszczenia), wynik, uczestnicy i otwarte usterki w chwili odbioru
      // LV (przedmiar): ostatnio wczytane zastępuje poprzednie; przypisanie kolumn obmiaru do pozycji LV dopisywane (null = bez pozycji)
      case TYPY.LV_WCZYTANE: S.lv = { id: d.id, nazwa: d.nazwa || 'LV', hash: d.hash || null, pozycje: d.pozycje || [], data: z.czasUrzadzenia }; break;
      case TYPY.LV_PRZYPISANE: Object.assign(S.lvPrzypisania, d.przypisania || {}); break;
      case TYPY.ODBIOR_ZAPISANY: S.odbiory[d.id] = { id: d.id, pietroId: d.pietroId || null, pomieszczenia: d.pomieszczenia || [], wynik: d.wynik, uczestnicy: d.uczestnicy || [], usterki: d.usterki || [], uwagi: d.uwagi || '', rodzaj: d.rodzaj || 'czesciowy', podpisy: d.podpisy || {}, data: z.czasUrzadzenia, autor: z.autor }; break;
      // zgłoszenie gotowości do odbioru (Fertigstellungsmitteilung): od niej biegną terminy § 12 VOB/B; data z formularza (pismo mogło wyjść wcześniej)
      case TYPY.GOTOWOSC_ZGLOSZONA: S.gotowosc[d.pietroId || 'budowa'] = { data: d.data, forma: d.forma || '', zgloszone: z.czasUrzadzenia, autor: z.autor }; break;
      case TYPY.USTERKA_USUNIETA: if (S.usterki[d.id]) Object.assign(S.usterki[d.id], { otwarta: false, zdjeciaPo: d.zdjecia || [], usunieta: z.czasUrzadzenia, usunal: z.autor }); break;
      default: break;
    }
    } catch (e) { S.bledne.push({ id: z.id, typ: z.typ, powod: e.message }); }
  }
  przypiszDoObrysow(S);
  return S;
}

// punkt z planu (kreator) leży w obrysie pomieszczenia tego samego piętra → należy do tego pomieszczenia (zamiast „najbliższego numeru”)
const wWielokacie = (x, y, W) => { let w = false; for (let i = 0, j = W.length - 1; i < W.length; j = i++) { const [xi, yi] = W[i], [xj, yj] = W[j]; if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) w = !w; } return w; };
function przypiszDoObrysow(S) {
  const zObrysem = Object.values(S.pomieszczenia).filter((q) => q.obrys?.length >= 3); if (!zObrysem.length) return;
  for (const p of Object.values(S.punkty)) {
    if (!p.zPlanu || p.zPlanu.x == null || p.przeniesiony || p.recznie) continue; const pietro = S.pomieszczenia[p.pomieszczenieId]?.pietroId;
    const q = zObrysem.find((r) => r.pietroId === pietro && wWielokacie(p.zPlanu.x, p.zPlanu.y, r.obrys));
    if (q) { p.pomieszczenieId = q.id; p.doSprawdzenia = false; p.wObrysie = true; }
  }
}

export const klucz = (obiektId, pracaId) => `${obiektId}|${pracaId}`;
export const zrobione = (S, obiektId, pracaId) => (S.odhaczenia[klucz(obiektId, pracaId)] || []).length > 0;

/** Lista budów z dziennika (do ekranu startowego). */
export function budowy(zdarzenia) {
  const ids = [...new Set(zdarzenia.filter((z) => z.typ === TYPY.BUDOWA_UTWORZONA).map((z) => z.budowaId))];
  return ids.map((id) => stan(zdarzenia, id)).map((S) => ({ ...S.budowa, ukryta: S.ukryta }))
    .sort((a, b) => (a.utworzona < b.utworzona ? 1 : -1));
}

function pick(o, klucze) { const r = {}; for (const k of klucze) if (o[k] !== undefined) r[k] = o[k]; return r; }

// ——— indeksy planu (A → B → C): nowy indeks to nowy dokument z `zastepuje`; piętro pokazuje najnowszy w łańcuchu ———
export function lancuchPlanow(S, pietroId) {
  const p = S.pietra[pietroId]; let d = p && S.dokumenty[p.dokumentId]; const L = [];
  const widziane = new Set();
  while (d && !widziane.has(d.id)) { widziane.add(d.id); L.push(d);
    const nast = Object.values(S.dokumenty).filter((x) => x.zastepuje === d.id && !x.ukryty).sort((a, b) => (a.dodany < b.dodany ? 1 : -1))[0]; d = nast; }
  return L; // od najstarszego do aktualnego
}
export const aktualnyPlan = (S, pietroId) => lancuchPlanow(S, pietroId).at(-1) || null;
export const nastepnyIndeks = (i) => (/^[A-Y]$/.test(i || '') ? String.fromCharCode(i.charCodeAt(0) + 1) : i ? `${i}+1` : 'B');

// indeks planu, według którego wykonano pracę albo pomiar (zapisywany w zdarzeniu; plan 4.6)
export function indeksPlanuDla(S, dane = {}) {
  const pk = S.punkty[dane.obiektId] || S.punkty[dane.punktId] || (dane.zmianaId && S.punkty[S.zmiany[dane.zmianaId]?.punktId]);
  const pom = S.pomieszczenia[dane.pomieszczenieId] || (pk && S.pomieszczenia[pk.pomieszczenieId]) || S.pomieszczenia[dane.obiektId];
  const trasa = S.trasy?.[dane.obiektId] || Object.values(S.trasy || {}).find((t) => t.odcinki.some((o) => o.id === dane.obiektId));
  const pktObwodu = dane.obwodId && Object.values(S.punkty).find((x) => x.obwodId === dane.obwodId && S.pomieszczenia[x.pomieszczenieId]);
  const pietroId = pom?.pietroId || trasa?.pietroId || (pktObwodu && S.pomieszczenia[pktObwodu.pomieszczenieId].pietroId);
  return pietroId ? aktualnyPlan(S, pietroId)?.indeks || null : null;
}
